import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";

const HowItWorksContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex-wrap: wrap;
  background-color: transparent;
  color: #fff;
  gap: 40px;
  padding: 80px 60px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 40px 20px;
  }
`;

const LeftColumn = styled.div`
  flex: 1;
  min-width: 300px;
`;

const Title = styled.h1`
  font-size: 48px;
  margin-bottom: 20px;
  color: ${({ theme }) => theme.black};

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const SubTitle = styled.p`
  font-size: 22px;
  margin-bottom: 20px;
  color: ${({ theme }) => theme.lightGray};

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const Steps = styled(motion.div)`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const StepItem = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid ${({ theme }) => theme.greenLight};
  border-radius: 20px;
  padding: 20px;
  background: ${({ theme }) =>
    theme.currentTheme === "light" ? "#fff" : "rgba(255, 255, 255, 0.3)"};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
    padding: 15px;
  }
`;

const StepNumber = styled.h2`
  color: #779bb8;
  font-size: 42px;
  font-weight: 700;
  margin-right: 20px;

  @media (max-width: 768px) {
    font-size: 32px;
    margin-bottom: 10px;
  }
`;

const StepImage = styled.div`
  img {
    width: 92px;
    max-width: 100%;

    @media (max-width: 768px) {
      width: 60px;
      margin-bottom: 10px;
    }
  }
`;

const StepText = styled.div`
  flex: 1;
  font-size: 22px;
  color: ${({ theme }) => theme.text};
  margin-left: 20px;

  @media (max-width: 768px) {
    font-size: 18px;
    margin-left: 0;
  }

  p {
    font-size: 16px;
    color: ${({ theme }) => theme.lightGray};

    @media (max-width: 768px) {
      font-size: 14px;
    }
  }
`;

const ImageContainer = styled.div`
  flex: 1;
  min-width: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 60px;

  @media (max-width: 768px) {
    padding-left: 0;
    margin-top: 40px;
  }
`;

const Image = styled(motion.img)`
  width: 100%;
  max-width: 500px;
  aspect-ratio: 1 / 1;
  height: auto;
`;

const stepVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 },
};

const stepsContainerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const HowItWorks = () => {
  return (
    <HowItWorksContainer
      id="tutorial"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: false, amount: 0.2 }}
      transition={{ duration: 0.6 }}
    >
      <LeftColumn>
        <Title>Let's see how MitterPay works</Title>
        <SubTitle>Why Individuals and Businesses prefer MitterPay</SubTitle>

        <Steps
          variants={stepsContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.2 }}
        >
          {[
            {
              number: "1",
              img: "/assets/img/PNG/Group 3673Connect_wallet_pic.png",
              title: "Connect Wallet",
              desc: "Connect your web3 wallet from which the payment is to be made",
            },
            {
              number: "2",
              img: "/assets/img/PNG/Group 3672Add_payment_pic (1).png",
              title: "Multi-send token",
              desc: "Create or upload receiving payment addresses to send money with one transaction hash",
            },
            {
              number: "3",
              img: "/assets/img/PNG/Group 3673Auth_payment_pic.png",
              title: "Airdrop Pool",
              desc: "Create an airdrop pool and upload all the receiving addresses, allowing the receiving addresses to claim their reward.",
            },
          ].map((step, index) => (
            <StepItem
              key={index}
              variants={stepVariants}
              transition={{ duration: 0.5 }}
            >
              <StepNumber>{step.number}</StepNumber>
              <StepImage>
                <img src={step.img} alt={step.title} />
              </StepImage>
              <StepText>
                {step.title}
                <p>{step.desc}</p>
              </StepText>
            </StepItem>
          ))}
        </Steps>
      </LeftColumn>

      <ImageContainer>
        <Image
          src="/assets/img/PNG/how_it_works_pic.svg"
          alt="How it works illustration"
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: false, amount: 0.2 }}
        />
      </ImageContainer>
    </HowItWorksContainer>
  );
};

export default HowItWorks;
